import { customField } from '@libs/domain';
import type { PageProps } from '@ps-smart-cp/ppfw-client';
import {
  ItemArray,
  Template,
  kebabCase,
  useGuideTab,
  useItemName,
  useLocalizedContents,
  useTry,
  useValidator,
  withRerender,
} from '@ps-smart-cp/ppfw-client';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useCustomFieldSettings } from '../../context';
import { CustomFieldBasicFieldSet, CustomFieldSettingFooterButton } from './components';
import { customFieldBasicSettingFieldConfig } from './item-config';
import type { CustomFieldBasicSetting } from './models';
import { CustomFieldBasicSettingRaw } from './models';
import { registerBasicSettings } from './repositories';

function PageComponent({ rerender, rerenderContentsOnly }: PageProps) {
  const LEDGER_INFO_SETTING_PATH = '/fa/custom-field-setting/ledger-connection-info';

  const getName = useItemName();
  const { basicSetting, refetch } = useCustomFieldSettings();
  const [settings, setSettings] = useState(basicSetting);
  const { setInfo } = useGuideTab();
  const { errorFields, validateThenTryRequest } = useTry();
  const { msg } = useLocalizedContents();
  const navigateToLedgerConnectionSetting = useNavigate();
  const validator = useValidator();

  const onRegisterClick = validateThenTryRequest(
    () =>
      validator
        .with(
          { settings },
          { settings: ItemArray.of<CustomFieldBasicSetting>(customFieldBasicSettingFieldConfig) },
        )
        .execute()
        .filter((fieldError) =>
          settings
            .map((setting, index) => (setting.isUsed ? index : -1))
            .filter((index) => index !== -1)
            .some((index) => fieldError.field.includes(index.toString())),
        )
        .map((fieldError) => ({ ...fieldError, field: kebabCase(`custom-field-${fieldError.field}`) })),
    async () => {
      await registerBasicSettings(
        CustomFieldBasicSettingRaw.fromModels(settings, [
          getName(customField.names.customField01),
          getName(customField.names.customField02),
          getName(customField.names.customField03),
          getName(customField.names.customField04),
          getName(customField.names.customField05),
        ]),
      );
      await refetch();
      setInfo([msg.ico13002]);
      rerenderContentsOnly();
    },
  );

  return (
    <Template
      title={getName(customField.titleNames.basicInfoSetting)}
      footerButtonGroup={
        <CustomFieldSettingFooterButton
          navigateButtonItemName={customField.buttonNames.toLedgerConnectionSetting}
          onNavigate={() => navigateToLedgerConnectionSetting(LEDGER_INFO_SETTING_PATH)}
          onRegister={onRegisterClick}
          onReset={rerender}
        />
      }
    >
      <CustomFieldBasicFieldSet errorFields={errorFields} settings={settings} setSettings={setSettings} />
    </Template>
  );
}

const BasicSetting = withRerender(PageComponent, 'BasicSetting');

export { BasicSetting };
